import { useAnalytics, type AnalyticsEvent } from '@/hooks/use-analytics';
import { useFormatPrice } from '@/hooks/use-brand';
import { useFavourites } from '@/hooks/use-favourites';
import { type ProfileOpenMode } from '@/hooks/use-profile-open-mode';
import { useVisitedProfiles } from '@/hooks/use-visited-profiles';
import { cn } from '@/lib/utils';
import { tv } from 'tailwind-variants';
import { type Profile } from '@/types';
import { BadgeCheck, Heart, Images, MapPin, Star } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProtectedImg } from './ui/protected-img';
import { AvailabilityBadge } from './availability-badge';
import { ProfileImages } from './profile-images';
import { StoryViewer, type StoryViewerProfile } from './story-viewer';

interface ProfileCardProps {
  profile: Profile;
  variant?: 'photo_first' | 'rich' | 'magazine';
  index?: number;
  openMode: ProfileOpenMode;
  onOpenModal: (id: string | number) => void;
  analyticsEvent?: AnalyticsEvent;
  analyticsMetadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Shared tv() variants
// ---------------------------------------------------------------------------

const overlayPill = tv({
  base: 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs backdrop-blur-sm',
  variants: {
    color: {
      dark: 'bg-black/60 font-medium text-white',
      primary: 'bg-primary/80 font-semibold text-primary-foreground',
    },
  },
  defaultVariants: { color: 'dark' },
});

const heartIcon = tv({
  base: 'h-4 w-4 transition-colors',
  variants: {
    liked: {
      true: 'fill-primary text-primary',
      false: 'text-white',
    },
  },
});

// ---------------------------------------------------------------------------
// Promotion helpers
// ---------------------------------------------------------------------------

/** CSS classes that express the promotion tier visually — glow + ring, no badge. */
export function promotionGlowClass(tier: 0 | 1 | 2 | 3 | undefined): string {
  switch (tier) {
    case 3:
      return 'ring-2 ring-primary/70 shadow-lg shadow-primary/25';
    case 2:
      return 'ring-1 ring-primary/50 shadow-md shadow-primary/15';
    case 1:
      return 'shadow-md shadow-primary/10';
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Shared hooks / helpers
// ---------------------------------------------------------------------------

function useCardInteraction(
  profile: Profile,
  openMode: ProfileOpenMode,
  onOpenModal: (id: string | number) => void,
  analyticsEvent: AnalyticsEvent,
  analyticsMetadata?: Record<string, unknown>,
) {
  const { track } = useAnalytics();
  const { markVisited } = useVisitedProfiles();

  const fireAnalytics = () =>
    track(analyticsEvent, {
      profile_id: profile.id,
      metadata: { open_mode: openMode, ...analyticsMetadata },
    });

  const handleCardClick = () => {
    fireAnalytics();
    if (profile.id != null) markVisited(profile.id);
    if (openMode === 'modal' && profile.id != null) onOpenModal(profile.id);
  };

  return { handleCardClick, fireAnalytics };
}

function CardWrapper({
  openMode,
  profileId,
  slug,
  handleCardClick,
  fireAnalytics,
  className,
  children,
}: {
  openMode: ProfileOpenMode;
  profileId: string | number | undefined;
  slug?: string;
  handleCardClick: () => void;
  fireAnalytics: () => void;
  className: string;
  children: React.ReactNode;
}) {
  const href = slug ? `/escort/${slug}` : `/p/${profileId}`;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
    if (openMode === 'modal') {
      e.preventDefault();
      handleCardClick();
    } else {
      fireAnalytics();
    }
  };

  return (
    <a
      data-testid="profile-card"
      href={href}
      target={openMode === 'new-tab' ? '_blank' : undefined}
      rel={openMode === 'new-tab' ? 'noopener noreferrer' : undefined}
      className={className}
      onClick={handleClick}
    >
      {children}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Engagement badge sub-components
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Badge atoms — no positioning, just appearance
// ---------------------------------------------------------------------------

function AvailableNowBadge({ profile }: { profile: Profile }) {
  const { t } = useTranslation('common');
  if (!profile.is_available_now) return null;
  return (
    <div className={overlayPill({ color: 'dark' })}>
      <span className="h-2 w-2 animate-pulse rounded-full bg-green-400" />
      {t('profile.available_now')}
    </div>
  );
}

function NewProfileBadge({ profile }: { profile: Profile }) {
  const { t } = useTranslation('common');
  if (!profile.created_at) return null;
  const isNew =
    Date.now() - new Date(profile.created_at).getTime() < 7 * 24 * 3_600_000;
  if (!isNew) return null;
  return (
    <div className={overlayPill({ color: 'primary' })}>
      {t('profile.new_badge')}
    </div>
  );
}

function RecentReviewBadge({ profile }: { profile: Profile }) {
  const { t } = useTranslation('common');
  if (!profile.has_recent_review || !profile.latest_review_at) return null;
  const hoursAgo = Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(profile.latest_review_at).getTime()) / 3_600_000,
    ),
  );
  return (
    <div className="flex items-center gap-1 text-xs text-green-400">
      <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
      {t('profile.new_review_ago', { hours: hoursAgo })}
    </div>
  );
}

function FavouriteButton({ profile }: { profile: Profile }) {
  const { t } = useTranslation('common');
  const { isFavourite, toggle } = useFavourites();
  if (profile.id == null) return null;
  const liked = isFavourite(profile.id);

  return (
    <button
      type="button"
      className="rounded-full bg-black/50 p-1.5 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 focus:opacity-100"
      aria-label={
        liked ? t('a11y.remove_from_favourites') : t('a11y.add_to_favourites')
      }
      onClick={(e) => {
        e.stopPropagation();
        toggle(profile.id!);
      }}
    >
      <Heart className={heartIcon({ liked })} />
    </button>
  );
}

function StoryRingButton({
  profile,
  priority,
}: {
  profile: Profile;
  priority?: boolean;
}) {
  const { t } = useTranslation('common');
  const [viewerOpen, setViewerOpen] = useState(false);
  const stories = profile.stories ?? [];
  if (stories.length === 0) return null;

  const viewerProfile: StoryViewerProfile = {
    id: profile.id!,
    slug: profile.slug,
    display_name: profile.display_name,
    avatar: profile.avatar,
    stories,
  };

  return (
    <>
      <button
        type="button"
        className="rounded-full bg-gradient-to-tr from-primary to-pink-400 p-0.5"
        aria-label={t('a11y.view_stories')}
        onClick={(e) => {
          e.stopPropagation();
          setViewerOpen(true);
        }}
      >
        <div className="h-7 w-7 overflow-hidden rounded-full border-2 border-black bg-card">
          {profile.gallery?.[0] && (
            <ProtectedImg
              src={profile.gallery[0].thumb}
              alt=""
              className="h-full w-full object-cover"
              loading={priority ? 'eager' : 'lazy'}
            />
          )}
        </div>
      </button>
      <StoryViewer
        profile={viewerProfile}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
      />
    </>
  );
}

function RatingBadge({ profile }: { profile: Profile }) {
  const count = profile.reviews_count as number | undefined;
  if (!count || count < 1) return null;
  return (
    <div className="flex items-center gap-1">
      <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
      <span className="text-sm font-medium">{profile.rating?.fmt_rating}</span>
      <span className="text-xs text-muted-foreground">({count})</span>
    </div>
  );
}

function FirstRate({
  profile,
  className,
}: {
  profile: Profile;
  className?: string;
}) {
  const formatPrice = useFormatPrice();
  const rate = profile?.rates?.in_call?.[0];
  if (!rate) return null;
  return (
    <div className={cn('text-sm', className)}>
      <span className="font-semibold">{formatPrice(Number(rate.price))}</span>
      <span className="text-muted-foreground">
        {' '}
        · {rate.formatted_duration}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slot-based image overlay
//
// Two absolute bars — top and bottom — each split into left/right zones.
// Every badge or button lives in exactly one zone; nothing ever overlaps.
//
//  ┌─[top-left]──────────[top-right]─┐
//  │  availability        VIP | New  │
//  │                                 │
//  │                                 │
//  └─[bot-left]──────────[bot-right]─┘
//     story ring          photo count
// ---------------------------------------------------------------------------

function ImageOverlays({ profile }: { profile: Profile }) {
  const isNew = profile.created_at
    ? Date.now() - new Date(profile.created_at).getTime() < 30 * 24 * 3_600_000
    : false;

  return (
    <>
      {/* Top bar */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-1 p-2.5">
        {/* Top-left: availability */}
        <div className="flex flex-col items-start gap-1">
          <AvailabilityBadge
            status={profile.availability_status}
            badge={profile.availability_badge}
          />
        </div>

        {/* Top-right: VIP + New + Touring (stacked, newest on top) */}
        <div className="pointer-events-auto flex flex-col items-end gap-1">
          {profile.verified && (
            <div className={overlayPill({ color: 'dark' })}>
              <BadgeCheck className="h-3.5 w-3.5 text-primary/80" />
              VIP
            </div>
          )}
          {isNew && <NewProfileBadge profile={profile} />}
          {profile.upcoming_tours && profile.upcoming_tours.length > 0 && (
            <div className={overlayPill({ color: 'primary' })}>
              <MapPin className="h-3 w-3" />
              Touring
            </div>
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-1 p-2">
        {/* Bottom-left: story ring */}
        <div className="pointer-events-auto">
          <StoryRingButton profile={profile} />
        </div>

        {/* Bottom-right: photo count + favourite */}
        <div className="pointer-events-auto flex items-center gap-1.5">
          <FavouriteButton profile={profile} />
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Visited dimming wrapper
// ---------------------------------------------------------------------------

function useVisitedDim(profileId: string | number | undefined): boolean {
  const { isVisited } = useVisitedProfiles();
  return profileId != null && isVisited(profileId);
}

// ---------------------------------------------------------------------------
// Variant A: PhotoFirstCard — full portrait, overlay text
// ---------------------------------------------------------------------------

function PhotoFirstCard({
  profile,
  index,
  openMode,
  onOpenModal,
  analyticsEvent,
  analyticsMetadata,
}: ProfileCardProps) {
  const { handleCardClick, fireAnalytics } = useCardInteraction(
    profile,
    openMode,
    onOpenModal,
    analyticsEvent!,
    analyticsMetadata,
  );
  const visited = useVisitedDim(profile.id);
  const priority = index !== undefined && index < 6;

  // listing_photos is the advertiser-curated subset of photos for cards.
  // Falls back to gallery so code works even if the backend hasn't sent it yet.
  const cardPhotos = profile.listing_photos ?? profile.gallery;
  const photo = cardPhotos?.[0];
  const formatPrice = useFormatPrice();
  const rate = profile?.rates?.in_call?.[0];

  const body = (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl transition-transform duration-200 hover:scale-[1.02] hover:ring-1 hover:ring-primary/30',
        visited && 'saturate-[75%]',
        promotionGlowClass(profile.promotion_tier),
      )}
    >
      <div className="aspect-[2/3] w-full overflow-hidden">
        {photo ? (
          <ProtectedImg
            src={photo.thumb || photo.url}
            alt={profile.display_name}
            className="h-full w-full object-cover"
            style={{ objectPosition: `${photo.focal_x}% ${photo.focal_y}%` }}
            loading={photo.loading ?? (priority ? 'eager' : 'lazy')}
          />
        ) : (
          <div className="h-full w-full bg-card" />
        )}
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      <ImageOverlays profile={profile} />

      <div className="absolute right-0 bottom-0 left-0 p-4">
        <div className="text-base font-bold text-white">
          {profile.display_name}
          {profile.age ? `, ${profile.age}` : ''}
        </div>
        {profile.city && (
          <div className="mt-0.5 flex items-center gap-1 text-xs text-white/70">
            <MapPin className="h-3 w-3 shrink-0" />
            <span>{profile.city}</span>
          </div>
        )}
        {rate && (
          <div className="mt-1 text-sm font-semibold text-primary/80">
            {formatPrice(Number(rate.price))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <CardWrapper
      openMode={openMode}
      profileId={profile.id}
      slug={profile.slug}
      handleCardClick={handleCardClick}
      fireAnalytics={fireAnalytics}
      className="block"
    >
      {body}
    </CardWrapper>
  );
}

// ---------------------------------------------------------------------------
// Variant B: RichCard — portrait photo + dark card body
// ---------------------------------------------------------------------------

function RichCard({
  profile,
  index,
  openMode,
  onOpenModal,
  analyticsEvent,
  analyticsMetadata,
}: ProfileCardProps) {
  const { handleCardClick, fireAnalytics } = useCardInteraction(
    profile,
    openMode,
    onOpenModal,
    analyticsEvent!,
    analyticsMetadata,
  );
  const visited = useVisitedDim(profile.id);
  const priority = index !== undefined && index < 6;

  const body = (
    <div
      className={cn(
        'group overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10',
        visited && 'saturate-[75%]',
        promotionGlowClass(profile.promotion_tier),
      )}
    >
      <div className="relative">
        <ProfileImages
          images={profile.listing_photos ?? profile.gallery}
          aspectClass="aspect-[3/4]"
          priority={priority}
          profileId={profile.id}
        />
        <ImageOverlays profile={profile} />
      </div>

      <div className="p-3">
        <h3 className="truncate text-sm leading-tight font-semibold text-foreground">
          {profile.display_name}
          {profile.age ? `, ${profile.age}` : ''}
        </h3>
        <div className="mt-1.5 space-y-1">
          <FirstRate profile={profile} />
          <RatingBadge profile={profile} />
          <RecentReviewBadge profile={profile} />
          {profile.city && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{profile.city}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <CardWrapper
      openMode={openMode}
      profileId={profile.id}
      slug={profile.slug}
      handleCardClick={handleCardClick}
      fireAnalytics={fireAnalytics}
      className="block"
    >
      {body}
    </CardWrapper>
  );
}

// ---------------------------------------------------------------------------
// Variant C: MagazineCard — RichCard but every 5th card is double-wide
// ---------------------------------------------------------------------------

function MagazineCard(props: ProfileCardProps) {
  const {
    profile,
    index = 0,
    openMode,
    onOpenModal,
    analyticsEvent,
    analyticsMetadata,
  } = props;
  const isWide = index > 0 && index % 5 === 0;
  const priority = index < 6;

  const { handleCardClick, fireAnalytics } = useCardInteraction(
    profile,
    openMode,
    onOpenModal,
    analyticsEvent!,
    analyticsMetadata,
  );
  const visited = useVisitedDim(profile.id);

  const cardPhotos = profile.listing_photos ?? profile.gallery;
  const photo = cardPhotos?.[0];
  const formatPrice = useFormatPrice();
  const rate = profile?.rates?.in_call?.[0];

  const sharedCardCls = cn(
    'group overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10',
    visited && 'saturate-[75%]',
    promotionGlowClass(profile.promotion_tier),
  );

  const regularBody = (
    <div className={sharedCardCls}>
      <div className="relative">
        <ProfileImages
          images={profile.listing_photos ?? profile.gallery}
          aspectClass="aspect-[3/4]"
          priority={priority}
          profileId={profile.id}
        />
        <ImageOverlays profile={profile} />
      </div>
      <div className="p-3">
        <h3 className="truncate text-sm leading-tight font-semibold text-foreground">
          {profile.display_name}
          {profile.age ? `, ${profile.age}` : ''}
        </h3>
        <div className="mt-1.5 space-y-1">
          <FirstRate profile={profile} />
          <RatingBadge profile={profile} />
          <RecentReviewBadge profile={profile} />
          {profile.city && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{profile.city}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const wideBody = (
    <div className={cn(sharedCardCls, 'md:flex')}>
      <div className="relative shrink-0 overflow-hidden md:w-1/2">
        <div className="aspect-[3/4] w-full overflow-hidden md:aspect-auto md:h-full">
          {photo ? (
            <ProtectedImg
              src={photo.thumb || photo.url}
              alt={profile.display_name}
              className="h-full w-full object-cover"
              style={{ objectPosition: `${photo.focal_x}% ${photo.focal_y}%` }}
              loading={photo.loading ?? (priority ? 'eager' : 'lazy')}
            />
          ) : (
            <div className="h-full w-full bg-background" />
          )}
        </div>
        <ImageOverlays profile={profile} />
      </div>

      <div className="flex flex-col justify-between p-5">
        <div>
          <h3 className="text-lg font-bold text-foreground">
            {profile.display_name}
            {profile.age ? `, ${profile.age}` : ''}
          </h3>
          {profile.city && (
            <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span>{profile.city}</span>
            </div>
          )}
          {profile.description && (
            <p className="mt-3 line-clamp-4 text-sm text-muted-foreground">
              {typeof profile.description === 'string'
                ? profile.description
                : String(Object.values(profile.description)[0])}
            </p>
          )}
        </div>
        <div className="mt-4 space-y-1.5">
          <RatingBadge profile={profile} />
          <RecentReviewBadge profile={profile} />
          {rate && (
            <div className="text-base font-bold text-primary/80">
              {formatPrice(Number(rate.price))}
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                · {rate.formatted_duration}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <CardWrapper
      openMode={openMode}
      profileId={profile.id}
      slug={profile.slug}
      handleCardClick={handleCardClick}
      fireAnalytics={fireAnalytics}
      className={cn('block', isWide && 'md:col-span-2')}
    >
      {isWide ? wideBody : regularBody}
    </CardWrapper>
  );
}

// ---------------------------------------------------------------------------
// Public export — picks variant based on prop, with per-profile override
// ---------------------------------------------------------------------------

export function ProfileCard(props: ProfileCardProps) {
  const { variant = 'rich', profile, ...rest } = props;

  // Priority order for variant resolution:
  // 1. promotion_tier ≥ 2 forces magazine (Featured / Spotlight cards are always wide)
  // 2. Per-profile card_variant override set by an admin
  // 3. Brand/page-level variant prop
  const tier = profile.promotion_tier ?? 0;
  const effectiveVariant: ProfileCardProps['variant'] =
    tier >= 2
      ? 'magazine'
      : ((profile.card_variant as
          | ProfileCardProps['variant']
          | null
          | undefined) ?? variant);

  if (effectiveVariant === 'photo_first')
    return <PhotoFirstCard profile={profile} {...rest} />;
  if (effectiveVariant === 'magazine')
    return <MagazineCard profile={profile} {...rest} />;
  return <RichCard profile={profile} {...rest} />;
}
